import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api.js'
import { CATEGORIES, SITE } from '../data/site.js'
import ProductImage from '../components/ProductImage.jsx'
import Icon from '../components/Icon.jsx'

const TRUST = [
  { icon: 'star', title: '15 years in glazing', text: 'Trade experience behind every product we stock.' },
  { icon: 'tag', title: 'Affordable, factory-direct', text: 'No middlemen — better value on every order.' },
  { icon: 'shield', title: 'AS/NZS 2208 certified', text: 'Toughened safety glass on every panel we sell.' },
  { icon: 'truck', title: 'Delivery or free pickup', text: 'Glass-safe delivery within 50 km, or free pickup in Ravenhall VIC.' },
]

const STEPS = [
  { n: 1, title: 'Choose your product', text: 'Browse shower screens, glass panels and hardware.' },
  { n: 2, title: 'Configure size & finish', text: 'Pick your height, configuration and hardware colour — price updates live.' },
  { n: 3, title: 'Secure checkout', text: 'Pay by bank transfer with a unique order reference.' },
  { n: 4, title: 'Pickup or delivery', text: 'Free warehouse pickup, or glass-safe delivery within 50 km.' },
]

// Home grid: two feature cards on top (Shower Screens + Wardrobe Doors), three below.
const HOME_CAT_ORDER = ['shower-screens', 'wardrobe-doors', 'hardware', 'glass-panels', 'tools-accessories']
const HOME_CATEGORIES = HOME_CAT_ORDER
  .map((slug) => CATEGORIES.find((c) => c.slug === slug))
  .filter(Boolean)
  .concat(CATEGORIES.filter((c) => !HOME_CAT_ORDER.includes(c.slug)))

const GALLERY_PREVIEW = [
  '/images/portfolio/level-up-frameless-corner-shower-screen-marble-melbourne-02.webp',
  '/images/portfolio/level-up-frameless-shower-screen-gold-fixtures-melbourne-01.webp',
  '/images/portfolio/level-up-black-framed-corner-shower-screen-marble-melbourne-01.webp',
  '/images/portfolio/level-up-sliding-glass-shower-screen-melbourne-01.webp',
]

export default function Home() {
  const [catImages, setCatImages] = useState({})
  useEffect(() => {
    api.settings().then((d) => {
      try { setCatImages(JSON.parse(d.settings.category_meta || '{}')) } catch { /* keep defaults */ }
    }).catch(() => {})
  }, [])
  const catImage = (c) => catImages[c.slug] || c.image

  return (
    <main>
      <section className="hero">
        <div className="hero-bg" style={{ backgroundImage: 'url(/images/portfolio/level-up-frameless-walk-in-shower-screen-melbourne-01.webp)' }} />
        <div className="hero-overlay" />
        <div className="container hero-inner">
          <div className="hero-content">
          <p className="hero-kicker">Melbourne · Delivery & warehouse pickup</p>
          <h1>Premium shower screens & glass, factory-direct</h1>
          <p className="hero-sub">
            15 years in the glazing industry — shower screens, wardrobe doors, toughened glass panels and
            hardware at affordable, factory-direct prices. All glass certified to AS/NZS 2208.
          </p>
          <div className="hero-actions">
            <Link to="/shop/shower-screens" className="btn btn-gold btn-lg">
              Shop shower screens <Icon name="arrowRight" size={18} />
            </Link>
            <Link to="/shop" className="btn btn-ghost btn-lg">Browse all products</Link>
          </div>
          <ul className="hero-points">
            <li><Icon name="check" size={16} /> 6mm & 10mm toughened safety glass</li>
            <li><Icon name="check" size={16} /> 6 hardware finishes</li>
            <li><Icon name="check" size={16} /> 2-year hardware warranty</li>
          </ul>
          </div>
        </div>
      </section>

      <section className="section trust-strip">
        <div className="container trust-grid">
          {TRUST.map((t) => (
            <div key={t.title} className="trust-item">
              <span className="trust-icon"><Icon name={t.icon} size={24} /></span>
              <div>
                <h3>{t.title}</h3>
                <p>{t.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="section-head">
            <h2>Shop by category</h2>
            <Link to="/shop" className="link-arrow">All products <Icon name="arrowRight" size={16} /></Link>
          </div>
          <div className="category-grid">
            {HOME_CATEGORIES.map((c, i) => (
              <Link key={c.slug} to={`/shop/${c.slug}`} className={`category-card ${i < 2 ? 'category-feature' : ''}`}>
                <div className="category-media">
                  <ProductImage src={catImage(c)} category={c.name} label={c.name} alt={c.name} loading="lazy" />
                </div>
                {i === 0 && <span className="category-badge">Best sellers</span>}
                <div className="category-card-body">
                  <h3>{c.name}</h3>
                  <p>{c.blurb}</p>
                  <span className="link-arrow">Shop now <Icon name="arrowRight" size={15} /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="section section-alt">
        <div className="container">
          <div className="section-head center">
            <h2>How buying works</h2>
            <p className="section-sub">
              From browsing to bathroom in four simple steps. <Link to="/how-to-buy">Read the full guide</Link>.
            </p>
          </div>
          <ol className="steps-grid">
            {STEPS.map((s) => (
              <li key={s.n} className="step-card">
                <span className="step-num">{s.n}</span>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="section-head">
            <h2>Recent installs by our customers</h2>
            <Link to="/gallery" className="link-arrow">Full gallery <Icon name="arrowRight" size={16} /></Link>
          </div>
          <div className="gallery-preview">
            {GALLERY_PREVIEW.map((src) => (
              <img key={src} src={src} alt="Frameless shower screen installation" loading="lazy" />
            ))}
          </div>
        </div>
      </section>

      <section className="section cta-band">
        <div className="container cta-inner">
          <div>
            <h2>Not sure which screen fits your space?</h2>
            <p>Send us your measurements and photos — we'll recommend the right configuration, free.</p>
          </div>
          <div className="cta-actions">
            <Link to="/contact" className="btn btn-light btn-lg">Get free advice</Link>
            <a href={SITE.phoneHref} className="btn btn-ghost-light btn-lg"><Icon name="phone" size={17} /> {SITE.phone}</a>
          </div>
        </div>
      </section>
    </main>
  )
}
